import { tenantLocale } from "./company";
/**
 * Owner-notice letter HTML builder (June 2026 rewrite).
 * Page 1: letterhead + owner + vehicle-details table (unchanged), then the
 * new notice wording supplied by the user. Page 2: printer-friendly
 * reproduction of NZTA Factsheet 63 (March 2025).
 * @page margin is 0 (browser header/footer suppressed); spacing comes from
 * per-page padding instead.
 */
export function buildOwnerLetterHtml(snap) {
  const origin = typeof window !== "undefined" && window.location ? window.location.origin : "";
  const factsheetSrc = `${origin}/factsheet-63.jpg`;
  const lh = snap.letterhead || {};
  const trade = (lh.trading_name || "").replace(/ Ltd$/i, "");
  const moneyFmt = (n) => `$${Number(n || 0).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const addressBlock = (snap.owner_address || "").includes("\n")
    ? snap.owner_address
    : (snap.owner_address || "").split(", ").join("\n");
  const isSixMonth = /6\s*month/i.test(snap.term_label || "");
  const deadlineDays = isSixMonth ? "38 days" : "10 days";
  return `<!doctype html>
<html><head><meta charset="utf-8">
<title>Owner Notice — ${snap.rego || ""}</title>
<style>
  @page { size: A4; margin: 0; }
  html, body { margin: 0; padding: 0; font-family: "Times New Roman", Georgia, serif; font-size: 11pt; color: #000; background: #fff; }
  /* Each .page is exactly one A4 sheet — content can never spill across. */
  .page { width: 210mm; height: 296mm; padding: 14mm 18mm; box-sizing: border-box; overflow: hidden; }
  .page2 { page-break-before: always; padding: 0; }
  .page2 img { width: 100%; height: 100%; object-fit: contain; display: block; }
  @media screen {
    body { background: #cbd5e1; }
    .page { margin: 10px auto; background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.25); }
  }
  .top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 22px; }
  .lh { text-align: right; }
  .trading { font-size: 20pt; font-weight: bold; letter-spacing: -0.3px; }
  .ta { margin-top: 6px; font-size: 11pt; }
  .lh-block { margin-top: 10px; line-height: 1.35; }
  .lh-email { font-style: italic; }
  .owner { margin-bottom: 18px; line-height: 1.45; }
  .owner .name { font-weight: bold; }
  .owner .addr { white-space: pre-line; }
  table.t { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 10.5pt; }
  table.t th, table.t td { border: 1px solid #555; padding: 7px 10px; text-align: left; }
  table.t th { background: #f1f5f9; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  p { margin: 0 0 9px; line-height: 1.45; }
  .b { font-weight: bold; }
  ul.bring { margin: 4px 0 0 18px; padding: 0; }
  ul.bring li { margin-bottom: 6px; line-height: 1.4; }
</style></head>
<body>

<div class="page">
  <div class="top">
    <div>${snap.generated_at_label || ""}</div>
    <div class="lh">
      <div class="trading">${lh.trading_name || ""}</div>
      <div class="ta">${lh.trading_as || ""}</div>
      <div class="lh-block">
        <div>${lh.address_line1 || ""}</div>
        <div>${lh.address_line2 || ""}</div>
        <div style="margin-top:4px">${lh.phone_primary || ""}</div>
        <div>${lh.phone_secondary || ""}</div>
        <div class="lh-email">${lh.email || ""}</div>
      </div>
    </div>
  </div>
  <div class="owner">
    <div class="name">${snap.owner_name || ""}</div>
    <div class="addr">${addressBlock}</div>
  </div>
  <table class="t">
    <thead><tr>
      <th>Date Impounded</th>
      <th>Vehicle Description</th>
      <th>Date Due Out</th>
      <th>Amount To Pay</th>
    </tr></thead>
    <tbody><tr>
      <td>${snap.impounded_at_label || ""}</td>
      <td>${snap.vehicle_description || ""}</td>
      <td>${snap.due_date_label || ""}</td>
      <td>${moneyFmt(snap.amount_due)}</td>
    </tr></tbody>
  </table>

  <p>Your vehicle has been impounded by the New Zealand Police. It is currently being held by <span class="b">${trade}</span> for a period of <span class="b">${snap.term_label || ""}</span>.</p>
  <p>If you <span class="b">DO NOT</span> intend to claim the vehicle at the end of the impound period, contact ${trade} as soon as possible to arrange signing over ownership of the vehicle in lieu of the impound and tow fees.</p>
  <p><span class="b">NO impounded vehicle will be released until payment has been made in full.</span> Vehicles will incur additional storage fees of ${moneyFmt(snap.daily_overstay_rate)} per day after the term of impound until payment is received in full.</p>
  <p>Please contact ${trade} within <span class="b">${deadlineDays}</span> to arrange payment or a payment plan for the impound and storage fees. We accept payment via EFTPOS, Cash or Direct Debit.</p>
  <p>If no contact is made within the above time periods required under the provisions of The Land Transport Amendment Act 2023 and any amendments, the vehicle will be considered &lsquo;abandoned&rsquo; and application will be made to dispose of the vehicle or transfer ownership of the vehicle to recover any impound fees.</p>
  <p>For a full summary please read the New Zealand Transport Agency Fact Sheet 63 attached.</p>
  <p>Please call ${trade} at least <span class="b">48 hours prior</span> to the release date of your impound to arrange collection of your vehicle.</p>
  <p class="b" style="margin-bottom:2px">What you will need to bring for collection of your vehicle.</p>
  <p style="margin-bottom:2px">Acceptable forms of ID and proof include:</p>
  <ul class="bring">
    <li><span class="b">Photo ID:</span> A current New Zealand driver licence, 18+ card, or a current passport.</li>
    <li><span class="b">Proof of Ownership:</span> Vehicle papers showing you are the registered owner/person at the time of impoundment.</li>
    <li><span class="b">Paperwork:</span> The police impoundment notice or reference number.</li>
  </ul>
</div>

<div class="page page2">
  <img src="${factsheetSrc}" alt="NZ Transport Agency Factsheet 63 — Impounding your vehicle at the roadside" />
</div>

</body></html>`;
}
